import {
	ButtonBase,
	CircularProgress,
	lighten,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { useGetDateString } from "web_myplann/Dashboard/Hooks/useGetDateString";
import { useGetDataByType } from "web_myplann/Dashboard/Hooks/useGetIconByType";
import { useNotificationClick } from "web_myplann/Dashboard/Hooks/useNotificationClick";
import type { NotificationListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { clampLinesSx } from "web_myplann/Shared/Utils/sxUtils";

export const NotificationListItem: React.FC<
	NotificationListItemResponseDTO
> = ({
	type,
	title,
	content,
	createdAt,
	readAt,
	id,
	sourceId,
	subContent,
	customContent,
}) => {
	const { palette } = useTheme();

	const { icon } = useGetDataByType(
		type,
		readAt ? palette.common.black : palette.primary.main,
	);
	const lightMain = lighten(
		palette.primary.main,
		palette.mode === "dark" ? 0.6 : 0.8,
	);
	const lightDesaturated = `color-mix(in oklch, ${lightMain} 85%, gray ${
		palette.mode === "dark" ? 50 : 5
	}%)`;
	const dateText = useGetDateString(createdAt);

	const { handleClick, isLoading } = useNotificationClick({
		type,
		id,
		sourceId,
		customContent,
	});

	return (
		<Stack
			direction="row"
			alignItems={subContent ? "flex-start" : "center"}
			gap={1.2}
			onClick={handleClick}
			component={ButtonBase}
			disabled={isLoading}
		>
			<Stack
				height={40}
				width={40}
				alignItems="center"
				justifyContent="center"
				borderRadius={9999}
				bgcolor={readAt ? palette.deprecated.grey[100] : lightDesaturated}
				flexShrink={0}
			>
				{isLoading ? <CircularProgress size={20} /> : icon}
			</Stack>

			<Stack flex={1} sx={{ minWidth: 0 }}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Typography
						variant="deprecatedBody2"
						color={palette.deprecated.grey[700]}
						lineHeight={"24px"}
					>
						{title}
					</Typography>
					<Typography
						variant={readAt ? "body1" : "body2"}
						color={readAt ? palette.deprecated.grey[700] : palette.primary.main}
					>
						{dateText}
					</Typography>
				</Stack>
				<Stack alignItems="flex-start" sx={{ width: "100%", minWidth: 0 }}>
					<Typography
						variant="deprecatedBody1"
						color={palette.deprecated.grey[500]}
						lineHeight={"24px"}
						sx={{
							...clampLinesSx(2),
							wordBreak: "break-word",
							maxWidth: "100%",
							textAlign: "start",
						}}
					>
						{content}
					</Typography>
				</Stack>
				<Stack alignItems="flex-start" sx={{ width: "100%", minWidth: 0 }}>
					<Typography
						variant="deprecatedBody1"
						color={palette.deprecated.grey[500]}
						lineHeight={"24px"}
					>
						{subContent}
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
};
